import React, { useState, useEffect } from 'react';
import { Calendar, Gift, Heart, Briefcase, Clock, Sparkles, ChevronRight, PartyPopper, Phone, Coffee, Users, ChevronLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import API_URL from '../config/API_URL';

const UpcomingDates = ({ onGenerateMessage, showSuccess, showError }) => {
  const [upcomingItems, setUpcomingItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generatingFor, setGeneratingFor] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isSectionOpen, setIsSectionOpen] = useState(true); // New state for section collapse
  const navigate = useNavigate();

  const ITEMS_PER_PAGE = 5;

  const styles = {
    container: {
      background: '#ffffff',
      borderRadius: '16px',
      padding: '0', // Changed to 0 since header will have padding
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)',
      border: '1px solid #e2e8f0',
      marginBottom: '2rem',
      overflow: 'hidden', // Added for smooth collapse
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '1.5rem',
      cursor: 'pointer', // Added cursor pointer
      transition: 'background 200ms',
      background: '#ffffff',
    },
    headerLeft: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.25rem',
      flex: 1,
    },
    headerRight: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
    },
    title: {
      fontSize: '1.25rem',
      fontFamily: "'Syne', sans-serif",
      fontWeight: 700,
      color: '#0f172a',
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
    },
    subtitle: {
      fontSize: '0.8rem',
      color: '#94a3b8',
      fontWeight: 400,
      paddingLeft: '2rem',
    },
    badge: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#667eea',
      color: 'white',
      borderRadius: '12px',
      padding: '0.25rem 0.75rem',
      fontSize: '0.85rem',
      fontWeight: 600,
    },
    collapseIcon: {
      color: '#64748b',
      transition: 'transform 200ms, color 200ms',
    },
    contentWrapper: {
      padding: '0 1.5rem 1.5rem 1.5rem',
    },
    datesList: {
      display: 'flex',
      flexDirection: 'column',
      gap: '0.75rem',
    },
    dateCard: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      padding: '1rem',
      background: '#f8fafc',
      borderRadius: '12px',
      border: '1px solid #e2e8f0',
      transition: 'all 200ms',
      cursor: 'pointer',
    },
    dateCardToday: {
      background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
      border: '2px solid #f59e0b',
      boxShadow: '0 4px 12px rgba(245, 158, 11, 0.2)',
    },
    dateCardThisWeek: {
      background: '#eff6ff',
      border: '1px solid #bfdbfe',
    },
    iconBox: {
      width: '48px',
      height: '48px',
      borderRadius: '12px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    },
    dateInfo: {
      flex: 1,
    },
    contactName: {
      fontSize: '1rem',
      fontWeight: 600,
      color: '#0f172a',
      marginBottom: '0.25rem',
      display: 'flex',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '0.5rem',
    },
    dateLabel: {
      fontSize: '0.85rem',
      color: '#64748b',
      marginBottom: '0.25rem',
    },
    daysUntil: {
      fontSize: '0.8rem',
      fontWeight: 600,
      color: '#667eea',
    },
    actionButtons: {
      display: 'flex',
      gap: '0.5rem',
      flexShrink: 0,
    },
    quickButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.5rem 0.875rem',
      border: 'none',
      borderRadius: '8px',
      fontSize: '0.85rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: 'white',
      boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)',
    },
    viewButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.35rem',
      padding: '0.5rem 0.875rem',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '0.85rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: '#ffffff',
      color: '#475569',
    },
    emptyState: {
      textAlign: 'center',
      padding: '2rem',
      color: '#64748b',
    },
    emptyIcon: {
      marginBottom: '0.75rem',
      opacity: 0.5,
    },
    loadingDots: {
      display: 'flex',
      gap: '0.5rem',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
    },
    loadingDot: {
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      background: '#667eea',
    },
    todayBanner: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.5rem',
      padding: '0.5rem 0.75rem',
      background: '#f59e0b',
      color: 'white',
      borderRadius: '6px',
      fontSize: '0.8rem',
      fontWeight: 600,
      marginBottom: '0.5rem',
    },
    sourceTag: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.15rem 0.5rem',
      borderRadius: '4px',
      fontSize: '0.7rem',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
    },
    importantDateTag: {
      background: '#fce7f3',
      color: '#be123c',
    },
    eventTag: {
      background: '#dbeafe',
      color: '#1e40af',
    },
    paginationContainer: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginTop: '1rem',
      padding: '0.75rem',
      background: '#f8fafc',
      borderRadius: '8px',
    },
    paginationInfo: {
      fontSize: '0.85rem',
      color: '#64748b',
      fontWeight: 500,
    },
    paginationButtons: {
      display: 'flex',
      gap: '0.5rem',
    },
    paginationButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.25rem',
      padding: '0.5rem 0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '0.85rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: '#ffffff',
      color: '#475569',
    },
    expandButton: {
      width: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '0.5rem',
      padding: '0.75rem',
      marginTop: '0.75rem',
      border: '2px solid #e2e8f0',
      borderRadius: '8px',
      fontSize: '0.85rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 200ms',
      fontFamily: "'Syne', sans-serif",
      background: '#ffffff',
      color: '#667eea',
    },
  };

  // ... (keep all existing useEffect and functions the same)

  useEffect(() => {
    loadUpcomingItems();
  }, []);

  const loadUpcomingItems = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/upcoming-all?days_ahead=60`, {
        credentials: 'include'
      });
      const data = await response.json();
      setUpcomingItems(data.upcoming || []);
    } catch (error) {
      console.error('Failed to load upcoming items:', error);
    } finally {
      setLoading(false);
    }
  };

  const getItemIcon = (item) => {
    if (item.source === 'important_date') {
      switch (item.type) {
        case 'birthday':
          return { Icon: Gift, color: '#ec4899', bg: '#fce7f3' };
        case 'anniversary':
          return { Icon: Heart, color: '#ef4444', bg: '#fee2e2' };
        case 'work_anniversary':
          return { Icon: Briefcase, color: '#8b5cf6', bg: '#f3e8ff' };
        default:
          return { Icon: Calendar, color: '#3b82f6', bg: '#dbeafe' };
      }
    }
    
    if (item.source === 'networking_event') {
      switch (item.type) {
        case 'call':
          return { Icon: Phone, color: '#667eea', bg: '#eef2ff' };
        case 'meeting':
          return { Icon: Briefcase, color: '#10b981', bg: '#d1fae5' };
        case 'coffee':
          return { Icon: Coffee, color: '#f59e0b', bg: '#fef3c7' };
        case 'followup':
          return { Icon: Clock, color: '#8b5cf6', bg: '#f3e8ff' };
        default:
          return { Icon: Calendar, color: '#64748b', bg: '#f1f5f9' };
      }
    }
    
    return { Icon: Calendar, color: '#3b82f6', bg: '#dbeafe' };
  };

  const getItemLabel = (item) => {
    if (item.source === 'important_date') {
      const typeNames = {
        birthday: 'Birthday',
        anniversary: 'Anniversary',
        work_anniversary: 'Work Anniversary',
        custom: 'Important Date'
      };
      return typeNames[item.type] || 'Important Date';
    }
    
    if (item.source === 'networking_event') {
      const typeNames = {
        call: 'Phone Call',
        meeting: 'Meeting',
        coffee: 'Coffee Chat',
        followup: 'Follow-up',
        other: 'Event'
      };
      return typeNames[item.type] || 'Event';
    }
    
    return 'Event';
  };

  const getDaysUntilText = (days) => {
    if (days === 0) return 'Today! 🎉';
    if (days === 1) return 'Tomorrow';
    if (days <= 7) return `In ${days} days`;
    return `${days} days away`;
  };

  const handleQuickGenerate = async (item, e) => {
    e.stopPropagation();
    setGeneratingFor(item.id);

    try {
      if (item.source === 'important_date') {
        const response = await fetch(`${API_URL}/api/contacts/${item.contact_id}/date-message`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({
            date_type: item.type,
            date_label: item.label || '',
            date_notes: item.notes || '',
            formatted_date: item.formatted_date,
            medium: 'text'
          })
        });

        const data = await response.json();
        
        if (response.ok) {
          const messageTypeMap = {
            birthday: 'thank_you',
            anniversary: 'thank_you',
            work_anniversary: 'thank_you',
            custom: 'check_in'
          };

          navigate('/dashboard/messages', {
            state: {
              selectedContact: item.contact_id,
              messageType: messageTypeMap[item.type] || 'check_in',
              medium: 'text',
              generatedMessage: data.message,
              context: `${data.occasion} - ${item.formatted_date}`
            }
          });
          
          const successMessages = {
            birthday: 'Birthday message generated! 🎂',
            anniversary: 'Anniversary message generated! 💕',
            work_anniversary: 'Work anniversary message generated! 🎉',
            custom: 'Message generated! ✨'
          };
          showSuccess(successMessages[item.type] || 'Message generated!');
        } else {
          showError(data.error || 'Failed to generate message');
        }
      } else if (item.source === 'networking_event') {
        navigate('/dashboard/calendar', {
          state: {
            highlightEvent: item.id,
            eventDetails: item
          }
        });
        showSuccess('Event details loaded!');
      }
    } catch (error) {
      console.error('Failed to handle item:', error);
      showError('Failed to process request');
    } finally {
      setGeneratingFor(null);
    }
  };

  const handleViewContact = (contactId, e) => {
    e.stopPropagation();
    navigate(`/dashboard/contacts/${contactId}`);
  };

  const LoadingDots = () => (
    <div style={styles.loadingDots}>
      <motion.div
        style={styles.loadingDot}
        animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0 }}
      />
      <motion.div
        style={styles.loadingDot}
        animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0.2 }}
      />
      <motion.div
        style={styles.loadingDot}
        animate={{ scale: [1, 1.3, 1], opacity: [1, 0.5, 1] }}
        transition={{ duration: 1, repeat: Infinity, delay: 0.4 }}
      />
    </div>
  );

  const importantDatesCount = upcomingItems.filter(i => i.source === 'important_date').length;
  const eventsCount = upcomingItems.filter(i => i.source === 'networking_event').length;

  const totalPages = Math.ceil(upcomingItems.length / ITEMS_PER_PAGE);
  const startIndex = currentPage * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const displayItems = isExpanded 
    ? upcomingItems.slice(startIndex, endIndex)
    : upcomingItems.slice(0, ITEMS_PER_PAGE);

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
    setCurrentPage(0);
  };

  const toggleSection = () => {
    setIsSectionOpen(!isSectionOpen);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.2 }}
      style={styles.container}
    >
      <div 
        style={styles.header}
        onClick={toggleSection}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = '#f8fafc';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = '#ffffff';
        }}
      >
        <div style={styles.headerLeft}>
          <h3 style={styles.title}>
            <Calendar size={24} />
            Upcoming Dates & Events
          </h3>
          {!loading && (
            <div style={styles.subtitle}>
              Looking 60 days ahead • {importantDatesCount} important {importantDatesCount === 1 ? 'date' : 'dates'}, {eventsCount} scheduled {eventsCount === 1 ? 'event' : 'events'}
            </div>
          )}
        </div>
        <div style={styles.headerRight}>
          {upcomingItems.length > 0 && (
            <div style={styles.badge}>
              {upcomingItems.length}
            </div>
          )}
          <div style={styles.collapseIcon}>
            {isSectionOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {isSectionOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{ overflow: 'hidden' }}
          >
            <div style={styles.contentWrapper}>
              {loading ? (
                <LoadingDots />
              ) : displayItems.length === 0 ? (
                <div style={styles.emptyState}>
                  <Calendar size={48} style={styles.emptyIcon} />
                  <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>
                    No upcoming items
                  </p>
                  <p style={{ fontSize: '0.85rem' }}>
                    Add important dates to contacts or schedule networking events
                  </p>
                </div>
              ) : (
                <>
                  <div style={styles.datesList}>
                    <AnimatePresence>
                      {displayItems.map((item, index) => {
                        const { Icon, color, bg } = getItemIcon(item);
                        const isToday = item.is_today;
                        const isThisWeek = item.is_this_week;

                        return (
                          <motion.div
                            key={item.id}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: index * 0.05 }}
                            style={{
                              ...styles.dateCard,
                              ...(isToday ? styles.dateCardToday : {}),
                              ...(isThisWeek && !isToday ? styles.dateCardThisWeek : {})
                            }}
                            onMouseEnter={(e) => {
                              if (!isToday) {
                                e.currentTarget.style.transform = 'translateX(4px)';
                                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.1)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isToday) {
                                e.currentTarget.style.transform = 'translateX(0)';
                                e.currentTarget.style.boxShadow = 'none';
                              }
                            }}
                            onClick={(e) => handleViewContact(item.contact_id, e)}
                          >
                            <div style={{ ...styles.iconBox, background: bg, color: color }}>
                              <Icon size={24} />
                            </div>

                            <div style={styles.dateInfo}>
                              {isToday && (
                                <div style={styles.todayBanner}>
                                  <PartyPopper size={14} />
                                  TODAY!
                                </div>
                              )}
                              <div style={styles.contactName}>
                                <span>{item.title}</span>
                                <span style={{
                                  ...styles.sourceTag,
                                  ...(item.source === 'important_date' ? styles.importantDateTag : styles.eventTag),
                                }}>
                                  {item.source === 'important_date' ? '📅 Date' : '🗓️ Event'}
                                </span>
                              </div>
                              <div style={styles.dateLabel}>
                                {getItemLabel(item)}
                                {item.label && ` • ${item.label}`}
                                {item.formatted_time && ` • ${item.formatted_time}`}
                              </div>
                              <div style={styles.daysUntil}>
                                {getDaysUntilText(item.days_until)}
                                {item.recurring && ' (Annual)'}
                              </div>
                            </div>

                            <div style={styles.actionButtons}>
                              <button
                                onClick={(e) => handleQuickGenerate(item, e)}
                                disabled={generatingFor === item.id}
                                style={{
                                  ...styles.quickButton,
                                  opacity: generatingFor === item.id ? 0.6 : 1,
                                  cursor: generatingFor === item.id ? 'not-allowed' : 'pointer',
                                }}
                                onMouseEnter={(e) => {
                                  if (generatingFor !== item.id) {
                                    e.currentTarget.style.transform = 'translateY(-2px)';
                                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (generatingFor !== item.id) {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                                  }
                                }}
                              >
                                <Sparkles size={14} />
                                {generatingFor === item.id 
                                  ? 'Processing...' 
                                  : item.source === 'important_date' 
                                    ? 'Generate Message' 
                                    : 'View Event'
                                }
                              </button>
                              <button
                                onClick={(e) => handleViewContact(item.contact_id, e)}
                                style={styles.viewButton}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = '#cbd5e1';
                                  e.currentTarget.style.background = '#f8fafc';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = '#e2e8f0';
                                  e.currentTarget.style.background = '#ffffff';
                                }}
                              >
                                <ChevronRight size={14} />
                                Contact
                              </button>
                            </div>
                          </motion.div>
                        );
                      })}
                    </AnimatePresence>
                  </div>

                  {upcomingItems.length > ITEMS_PER_PAGE && (
                    <>
                      {!isExpanded ? (
                        <button
                          onClick={handleToggleExpand}
                          style={styles.expandButton}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#cbd5e1';
                            e.currentTarget.style.background = '#f8fafc';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#e2e8f0';
                            e.currentTarget.style.background = '#ffffff';
                          }}
                        >
                          <ChevronRight size={16} style={{ transform: 'rotate(90deg)' }} />
                          Show all {upcomingItems.length} items
                        </button>
                      ) : (
                        <div style={styles.paginationContainer}>
                          <div style={styles.paginationInfo}>
                            Showing {startIndex + 1}-{Math.min(endIndex, upcomingItems.length)} of {upcomingItems.length}
                          </div>
                          <div style={styles.paginationButtons}>
                            <button
                              onClick={handlePrevPage}
                              disabled={currentPage === 0}
                              style={{
                                ...styles.paginationButton,
                                opacity: currentPage === 0 ? 0.5 : 1,
                                cursor: currentPage === 0 ? 'not-allowed' : 'pointer',
                              }}
                              onMouseEnter={(e) => {
                                if (currentPage !== 0) {
                                  e.currentTarget.style.borderColor = '#cbd5e1';
                                  e.currentTarget.style.background = '#f8fafc';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (currentPage !== 0) {
                                  e.currentTarget.style.borderColor = '#e2e8f0';
                                  e.currentTarget.style.background = '#ffffff';
                                }
                              }}
                            >
                              <ChevronLeft size={16} />
                              Previous
                            </button>
                            <button
                              onClick={handleNextPage}
                              disabled={currentPage >= totalPages - 1}
                              style={{
                                ...styles.paginationButton,
                                opacity: currentPage >= totalPages - 1 ? 0.5 : 1,
                                cursor: currentPage >= totalPages - 1 ? 'not-allowed' : 'pointer',
                              }}
                              onMouseEnter={(e) => {
                                if (currentPage < totalPages - 1) {
                                  e.currentTarget.style.borderColor = '#cbd5e1';
                                  e.currentTarget.style.background = '#f8fafc';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (currentPage < totalPages - 1) {
                                  e.currentTarget.style.borderColor = '#e2e8f0';
                                  e.currentTarget.style.background = '#ffffff';
                                }
                              }}
                            >
                              Next
                              <ChevronRight size={16} />
                            </button>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default UpcomingDates;